import { create } from "zustand";

import { getPatients, getPatientsCount, getPatientsPaginated } from "@/app/actions/patient-actions";
import type { Patient } from "@/types";

const PATIENTS_PAGE_SIZE = 50;

type PatientsState = {
  patients: Patient[];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  lastFetched: number | null;
  currentOrganizationId: string | null;
  currentSearch: string | null;
  // Monotonic counter — every fetchPatients call bumps it and only applies its
  // response if it's still the latest. Drops stale responses (e.g. an in-flight
  // load-more landing after a newer search).
  requestId: number;
  fetchPatients: (
    includeHidden?: boolean,
    search?: string,
    organizationId?: string,
    options?: { append?: boolean }
  ) => Promise<void>;
  fetchPatientsCount: (organizationId?: string) => Promise<void>;
  setPatients: (patients: Patient[]) => void;
  addPatient: (patient: Patient) => void;
  updatePatient: (patientId: string, updates: Partial<Patient>) => void;
  removePatient: (patientId: string) => void;
  clearError: () => void;
  clearPatients: () => void;
  invalidateCache: () => void;
  setOrganization: (organizationId: string | null) => void;
};

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

export const usePatientsStore = create<PatientsState>((set, get) => ({
  patients: [],
  totalCount: 0,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  lastFetched: null,
  currentOrganizationId: null,
  currentSearch: null,
  requestId: 0,

  fetchPatients: async (
    _includeHidden = false,
    search?: string,
    organizationId?: string,
    options?: { append?: boolean }
  ) => {
    const state = get();
    const append = options?.append === true;
    const orgId = organizationId ?? state.currentOrganizationId;

    if (append) {
      // Never overlap an append with another append OR with a list-replacing fetch
      // (search/org-change). Without the isLoading guard, a load-more can fire while
      // a search is in flight, bump the request generation past the search, get its
      // own response dropped by a subsequent fetch, and leave the spinner stuck on.
      if (!state.hasMore || state.isLoadingMore || state.isLoading) return;
      set({ isLoadingMore: true, error: null });
    } else {
      const orgChanged = organizationId !== undefined && organizationId !== state.currentOrganizationId;
      const searchChanged = (search ?? null) !== (state.currentSearch ?? null);
      if (!orgChanged && !searchChanged && state.lastFetched && Date.now() - state.lastFetched < STALE_TIME) {
        return;
      }
      // Any in-flight load-more becomes moot when we replace the list — clear its
      // spinner immediately so it doesn't linger after this fetch completes.
      set({ isLoading: true, isLoadingMore: false, error: null });
    }

    // Bump the generation counter; only the matching response will be applied below.
    const myRequestId = state.requestId + 1;
    set({ requestId: myRequestId });

    try {
      const offset = append ? state.patients.length : 0;
      const effectiveSearch = search ?? (append ? state.currentSearch : undefined);
      const { patients: nextPatients, totalCount } = await getPatientsPaginated(effectiveSearch ?? undefined, {
        limit: PATIENTS_PAGE_SIZE,
        offset,
      });

      // Drop stale responses (a newer fetch has already started).
      if (get().requestId !== myRequestId) return;

      const now = Date.now();
      if (append) {
        set((s) => ({
          patients: [...s.patients, ...nextPatients],
          hasMore: offset + nextPatients.length < totalCount,
          isLoadingMore: false,
          lastFetched: now,
        }));
      } else {
        set({
          patients: nextPatients,
          totalCount,
          hasMore: nextPatients.length < totalCount,
          isLoading: false,
          isLoadingMore: false,
          lastFetched: now,
          currentOrganizationId: orgId ?? state.currentOrganizationId,
          currentSearch: search ?? null,
        });
      }
    } catch (error) {
      // Still drop stale errors so we don't surface them after a newer fetch.
      if (get().requestId !== myRequestId) return;
      set({
        isLoading: false,
        isLoadingMore: false,
        error: error instanceof Error ? error.message : "Failed to fetch patients",
      });
    }
  },

  fetchPatientsCount: async () => {
    try {
      const totalCount = await getPatientsCount();
      set({ totalCount });
    } catch {
      set({ totalCount: 0 });
    }
  },

  setPatients: (patients) => {
    set({ patients, lastFetched: Date.now() });
  },

  addPatient: (patient) => {
    set((state) => ({
      patients: [patient, ...state.patients],
      totalCount: state.totalCount + 1,
    }));
  },

  updatePatient: (patientId, updates) => {
    set((state) => ({
      patients: state.patients.map((patient) => (patient.id === patientId ? { ...patient, ...updates } : patient)),
    }));
  },

  removePatient: (patientId) => {
    set((state) => ({
      patients: state.patients.filter((patient) => patient.id !== patientId),
      totalCount: Math.max(0, state.totalCount - 1),
    }));
  },

  clearError: () => {
    set({ error: null });
  },

  clearPatients: () => {
    set({
      patients: [],
      lastFetched: null,
      currentOrganizationId: null,
      currentSearch: null,
      totalCount: 0,
      hasMore: false,
    });
  },

  invalidateCache: () => {
    set({ lastFetched: null });
  },

  setOrganization: (organizationId: string | null) => {
    const state = get();
    if (organizationId !== state.currentOrganizationId) {
      set({
        currentOrganizationId: organizationId,
        lastFetched: null,
        patients: [],
        totalCount: 0,
        hasMore: false,
        currentSearch: null,
      });
    }
  },
}));
